import React, { useState } from "react";
import { Link } from "react-router";
import { Calendar, Clock, ArrowRight, Search } from "lucide-react";
import { MikassaLogo } from "@/components/mikassa/Logo";
import SEO from "@/components/SEO";
import blogData from "@/data/blog-posts.json";

// Get French posts from generated data
const ALL_POSTS = blogData.fr.map((post, index) => ({
  id: String(index + 1).padStart(3, '0'),
  slug: post.slug,
  title: post.title,
  description: post.description,
  category: post.category,
  date: post.date,
  readTime: post.readTime,
  image: post.coverImage.startsWith('/')
    ? `https://images.pexels.com/photos/5642426/pexels-photo-5642426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600`
    : post.coverImage,
}));

// Extract unique categories
const CATEGORIES = ["Tous", ...new Set(ALL_POSTS.map(p => p.category))];

const BlogCard = ({ post }) => {
  return (
    <Link to={`/blog/${post.slug}`} className="group block">
      <article className="h-full flex flex-col bg-white border border-[#0C2340]/8 overflow-hidden hover:border-[#E8611A]/50 transition-colors duration-500">
        <div className="relative overflow-hidden aspect-[16/10]">
          <img
            src={post.image}
            alt={post.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute top-4 left-4">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E8611A] text-white font-body text-[0.65rem] uppercase tracking-[0.2em]">
              {post.category}
            </span>
          </div>
        </div>
        <div className="flex-1 flex flex-col p-6">
          <div className="flex items-center gap-4 mb-4 text-[#636E72]">
            <span className="flex items-center gap-1.5 font-body text-[0.65rem] uppercase tracking-[0.2em]">
              <Calendar size={12} />
              {post.date}
            </span>
            <span className="flex items-center gap-1.5 font-body text-[0.65rem] uppercase tracking-[0.2em]">
              <Clock size={12} />
              {post.readTime}
            </span>
          </div>
          <h3 className="font-heading font-bold text-[#0C2340] text-lg md:text-xl leading-tight mb-3 group-hover:text-[#E8611A] transition-colors">
            {post.title}
          </h3>
          <p className="font-body text-[#2D3436] text-sm leading-relaxed mb-4 flex-1 line-clamp-3">
            {post.description}
          </p>
          <div className="flex items-center gap-2 font-body text-[0.7rem] uppercase tracking-[0.2em] font-semibold text-[#E8611A] group-hover:gap-3 transition-all">
            Lire l'article
            <ArrowRight size={14} />
          </div>
        </div>
      </article>
    </Link>
  );
};

const BlogList = () => {
  const [activeCategory, setActiveCategory] = useState("Tous");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredPosts = ALL_POSTS.filter((post) => {
    const matchesCategory = activeCategory === "Tous" || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <SEO
        title="Blog — Conseils & Actualités Immobilières"
        description="Découvrez nos articles, guides et analyses pour optimiser votre gestion locative et rester informé des tendances du marché immobilier en Afrique."
        url="/blog"
        breadcrumbs={[
          { name: "Accueil", url: "/" },
          { name: "Blog", url: "/blog" }
        ]}
      />
      {/* Header */}
      <header className="bg-[#0C2340] text-white py-6">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16 flex items-center justify-between">
          <Link to="/">
            <MikassaLogo inverse />
          </Link>
          <Link to="/" className="font-body text-[0.72rem] uppercase tracking-[0.22em] font-medium text-white/85 hover:text-white transition-colors">
            Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-[#0C2340] text-white pt-16 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-2 h-2 rotate-45 bg-[#E8611A]" />
            <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-white/60">
              Blog & Ressources
            </span>
          </div>
          <h1 className="font-heading font-black text-white text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
            Conseils & actualités<br />
            <span className="text-[#E8611A]">pour les pros de l'immobilier.</span>
          </h1>
          <p className="font-body text-white/70 text-base md:text-lg max-w-2xl">
            Découvrez nos articles, guides et analyses pour optimiser votre gestion
            locative et rester informé des tendances du marché.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-[#0C2340]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16 py-6">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 font-body text-[0.7rem] uppercase tracking-[0.2em] transition-colors ${
                    activeCategory === cat
                      ? "bg-[#E8611A] text-white"
                      : "bg-white border border-[#0C2340]/15 text-[#0C2340] hover:border-[#E8611A]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-auto">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#636E72]" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full md:w-64 pl-10 pr-4 py-2.5 border border-[#0C2340]/15 font-body text-sm focus:outline-none focus:border-[#E8611A] transition-colors"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-16 py-16 md:py-24">
        {filteredPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredPosts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="font-body text-[#636E72] text-lg">
              Aucun article trouvé pour cette recherche.
            </p>
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="bg-[#0C2340] text-white py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16 text-center">
          <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mb-4">
            Prêt à simplifier votre gestion locative ?
          </h2>
          <p className="font-body text-white/70 mb-8 max-w-xl mx-auto">
            Découvrez comment Mikassa Agency peut transformer votre activité.
          </p>
          <Link to="/#contact" className="mikassa-btn-primary inline-flex">
            Demander une démo gratuite
            <span className="w-2 h-2 rotate-45 bg-white ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogList;
